// src/components/Blog.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Blog.css';
import BubbleNetwork from '../components/logo/BubbleNetwork';

const Blog = () => {
  const navigate = useNavigate();
  const [showFeaturedArticle, setShowFeaturedArticle] = useState(false);
  const [showCollaborationArticle, setShowCollaborationArticle] = useState(false);
  const [showAiRagArticle, setShowAiRagArticle] = useState(false);
  const [showSecurityArticle, setShowSecurityArticle] = useState(false);
  const [showProductivityArticle, setShowProductivityArticle] = useState(false);
  const [showExecutionArticle, setShowExecutionArticle] = useState(false);
  const [showKnowledgeBaseArticle, setShowKnowledgeBaseArticle] = useState(false);
  const [expandedGuideTitle, setExpandedGuideTitle] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('All Topics');
  const [subscriberEmail, setSubscriberEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState({ type: '', message: '' });
  const [isSubscribing, setIsSubscribing] = useState(false);
  const apiBaseUrl = process.env.REACT_APP_SERVER_URL || '';

  const featuredPost = {
    title: 'How Medhas AI Is Transforming Team Collaboration',
    summary:
      'Discover how AI-guided workflows, secure collaboration, and role-aware insights help teams move faster without losing context.',
    category: 'Product Insights',
    readTime: '4 min read',
    date: 'March 13, 2026'
  };

  const blogPosts = [
    {
      title: '5 Ways To Improve Cross-Functional Communication',
      summary:
        'Practical patterns to remove handoff delays, reduce misunderstandings, and keep every team aligned.',
      category: 'Collaboration',
      readTime: '3 min read',
      date: 'March 10, 2026'
    },
    {
      title: 'Building Trustworthy AI Responses With Citations',
      summary:
        'Why source-backed answers matter and how Medhas AI helps teams verify responses with confidence.',
      category: 'AI & RAG',
      readTime: '4 min read',
      date: 'March 7, 2026'
    },
    {
      title: 'Secure Collaboration: What Every Modern Team Needs',
      summary:
        'A practical checklist for authentication, access control, and safe sharing in AI-first products.',
      category: 'Security',
      readTime: '2 min read',
      date: 'March 4, 2026'
    },
    {
      title: 'AI Workflow Templates For Product Teams',
      summary:
        'Ready-to-use templates that help product, design, and engineering teams stay aligned from planning to release.',
      category: 'Productivity',
      readTime: '3 min read',
      date: 'March 2, 2026'
    },
    {
      title: 'From Meetings To Action: Turning Notes Into Decisions',
      summary:
        'A repeatable method for extracting action items, ownership, and deadlines directly from team conversations.',
      category: 'Execution',
      readTime: '4 min read',
      date: 'February 27, 2026'
    },
    {
      title: 'Scaling Internal Knowledge Across Distributed Teams',
      summary:
        'How to keep institutional knowledge searchable, trustworthy, and accessible as teams and projects grow.',
      category: 'Knowledge Base',
      readTime: '3 min read',
      date: 'February 22, 2026'
    }
  ];

  const topicFilters = ['All Topics', ...new Set(blogPosts.map((post) => post.category))];

  const filteredPosts =
    selectedTopic === 'All Topics'
      ? blogPosts
      : blogPosts.filter((post) => post.category === selectedTopic);

  const guidePosts = [
    {
      title: 'The 30-Minute Team Alignment Playbook',
      detail:
        'A quick weekly format to align priorities, blockers, and ownership without meeting overload.',
      meta: 'Guide • 1 min read'
    },
    {
      title: 'Getting Better Answers From AI Prompts',
      detail:
        'Prompt frameworks your team can standardize to improve quality, consistency, and traceability.',
      meta: 'Guide • 2 min read'
    },
    {
      title: 'Operational Checklist For Secure Knowledge Sharing',
      detail:
        'Control permissions, verify sources, and safely share AI outputs across internal and external stakeholders.',
      meta: 'Checklist • 2 min read'
    }
  ];

  const guideArticleDetails = {
    'The 30-Minute Team Alignment Playbook': {
      intro:
        'This playbook turns weekly alignment into a focused ritual that improves visibility and ownership without meeting fatigue.',
      sections: [
        {
          heading: 'Set a Fixed Weekly Cadence',
          content:
            'Run the session on the same day and time each week with a clear agenda: priorities, blockers, ownership, and risks.'
        },
        {
          heading: 'Use a Single Shared Board',
          content:
            'Capture top initiatives in one place so every team can see status, dependencies, and next actions at a glance.'
        },
        {
          heading: 'End with Clear Commitments',
          content:
            'Assign owners and due dates for each action item, then publish outcomes immediately so there is no ambiguity.'
        },
        {
          heading: 'Track Blockers in Real Time',
          content:
            'Flag blockers during the session and categorize them by team, dependency, and urgency so they can be resolved quickly.'
        },
        {
          heading: 'Close the Loop Next Week',
          content:
            'Start each new alignment by reviewing last week\'s commitments to ensure progress, accountability, and continuous improvement.'
        }
      ],
      summary:
        'A short, structured alignment system keeps teams synchronized while preserving deep work time.',
      takeaway:
        'When repeated consistently, this 30-minute format reduces rework, speeds handoffs, and helps every team member stay outcome-focused.'
    },
    'Getting Better Answers From AI Prompts': {
      intro:
        'Teams get more reliable AI output when they standardize prompt quality, context depth, and output constraints.',
      sections: [
        {
          heading: 'Define Intent and Audience',
          content:
            'Start prompts by stating the business goal, target audience, and expected tone to reduce generic or off-target output.'
        },
        {
          heading: 'Provide Context and Constraints',
          content:
            'Include product details, domain constraints, and success criteria so the model can reason within the right boundaries.'
        },
        {
          heading: 'Standardize Output Format',
          content:
            'Request structured responses such as bullet lists, tables, or action plans to improve review speed and consistency.'
        },
        {
          heading: 'Include Examples and Counter-Examples',
          content:
            'Provide one good example and one unacceptable output example to guide the model toward the expected response quality.'
        },
        {
          heading: 'Review and Iterate Prompt Versions',
          content:
            'Treat prompts as reusable assets: test, measure output quality, and refine wording over time for stronger reliability.'
        }
      ],
      summary:
        'Prompt frameworks make AI responses more predictable, usable, and easier to validate across teams.',
      takeaway:
        'A disciplined prompt workflow helps teams reduce hallucinations, improve response consistency, and accelerate decision cycles.'
    },
    'Operational Checklist For Secure Knowledge Sharing': {
      intro:
        'This checklist helps teams share AI-assisted knowledge safely while maintaining governance, traceability, and compliance.',
      sections: [
        {
          heading: 'Apply Role-Based Access Controls',
          content:
            'Grant least-privilege access to documents, prompts, and outputs so sensitive information is only visible to the right users.'
        },
        {
          heading: 'Verify Sources Before Sharing',
          content:
            'Confirm citations, data freshness, and confidence before distributing outputs internally or externally.'
        },
        {
          heading: 'Maintain Audit and Review Workflows',
          content:
            'Track who generated, reviewed, and approved critical knowledge artifacts to support accountability and compliance.'
        },
        {
          heading: 'Classify Information by Sensitivity',
          content:
            'Label content by confidentiality level and apply handling rules so sensitive data is never shared outside approved boundaries.'
        },
        {
          heading: 'Use Expiration and Revalidation Rules',
          content:
            'Set review intervals for critical knowledge assets to ensure outdated or inaccurate guidance is updated before reuse.'
        }
      ],
      summary:
        'Secure knowledge sharing combines strong permissions, source validation, and auditable review processes.',
      takeaway:
        'With operational guardrails in place, teams can move fast with AI-assisted content while still protecting trust and compliance.'
    }
  };

  const fullFeaturedArticle = [
    {
      heading: 'Real-time Communication',
      content:
        'Medhas AI integrates seamlessly with popular communication platforms like Slack, Microsoft Teams, and Google Chat, enabling instant messaging, file sharing, and video conferencing among team members. This real-time communication streamlines the flow of information, reducing delays and miscommunications.'
    },
    {
      heading: 'Intelligent Meeting Assistance',
      content:
        'With Medhas AI, meetings become more productive as our technology helps schedule, manage, and even transcribe discussions. By automatically summarizing key points, action items, and decisions, we ensure that everyone stays on the same page without having to manually take notes.'
    },
    {
      heading: 'Task Management',
      content:
        'Our intelligent task management system enables team members to assign, prioritize, and track tasks more effectively. By integrating with project management tools like Asana, Trello, and Jira, Medhas AI makes it easier for teams to collaborate on projects of any size.'
    },
    {
      heading: 'Predictive Analytics',
      content:
        'Leveraging machine learning algorithms, Medhas AI is capable of predicting potential roadblocks and offering solutions proactively. This feature empowers teams to address issues before they become critical, ultimately saving time and resources.'
    },
    {
      heading: 'Natural Language Processing (NLP)',
      content:
        'By understanding the context and sentiment of conversations, Medhas AI can help facilitate more productive discussions by flagging potential conflicts or miscommunications in real-time. This capability ensures that team members maintain a positive working relationship even when dealing with complex projects or tight deadlines.'
    },
    {
      heading: 'Continuous Learning',
      content:
        'As Medhas AI interacts with more users and processes more data, it continuously learns and adapts to better understand each individual\'s communication style, preferences, and needs. This ongoing learning process ensures that our technology becomes an increasingly valuable asset for teams over time.'
    }
  ];

  const collaborationArticleSections = [
    {
      heading: 'Establish Clear Goals and Objectives',
      content:
        'Ensure each team member understands their role and how it contributes to the overall goals of the project. Clarity in roles and responsibilities supports better collaboration and stronger communication.'
    },
    {
      heading: 'Promote Open Dialogue',
      content:
        'Encourage a culture where team members feel comfortable sharing ideas, concerns, and feedback. Regular meetings, brainstorming sessions, and an open-door discussion style can help make communication more consistent.'
    },
    {
      heading: 'Implement Collaborative Tools',
      content:
        'Use project management tools, shared documents, and communication platforms that make it simple for teams to exchange information and stay aligned on progress.'
    },
    {
      heading: 'Encourage Empathy and Understanding',
      content:
        'Help team members understand each other\'s roles, challenges, and perspectives. This improves collaboration by making everyone more aware of how their actions impact others.'
    },
    {
      heading: 'Provide Training and Resources',
      content:
        'Invest in communication training such as active listening, problem-solving, and conflict resolution. Resources like communication guides or team charters can also set clear expectations across teams.'
    }
  ];

  const aiRagArticleSections = [
    {
      heading: 'Research Reliable Sources',
      content:
        'Start by identifying reputable sources such as peer-reviewed academic journals, books authored by field experts, and trusted news outlets. Reliable sources improve the quality and trustworthiness of AI responses.'
    },
    {
      heading: 'Use Appropriate Citation Styles',
      content:
        'Choose a citation style that is widely accepted in your field or organization, such as APA, MLA, or Chicago. Keep citations consistent throughout the response to maintain clarity and credibility.'
    },
    {
      heading: 'Cite Primary Sources Whenever Possible',
      content:
        'Prefer primary references like original studies, interviews, and source publications. Primary sources are generally more reliable than summaries or second-hand commentary.'
    },
    {
      heading: 'Verify Information',
      content:
        'Cross-check important facts against multiple credible sources before presenting them. Verification reduces the chance of errors and helps prevent misinformation.'
    },
    {
      heading: 'Provide Clear Attribution',
      content:
        'Clearly attribute direct quotes, statistics, and borrowed ideas. Proper attribution gives credit to original authors and strengthens transparency for readers.'
    },
    {
      heading: 'Keep Citations Updated',
      content:
        'Review and refresh citations regularly so they remain current and valid. Outdated references can weaken confidence in AI-generated responses.'
    }
  ];

  const securityArticleSections = [
    {
      heading: 'Secure Collaboration: The Basis for Trust and Productivity',
      content:
        'Secure collaboration platforms help teams work smoothly, share information efficiently, and maintain transparency. Strong security controls are essential because data privacy breaches can lead to financial losses, reputational damage, and legal exposure.'
    },
    {
      heading: 'MedhasAI: Enhancing Security Through Adaptive AI',
      content:
        'MedhasAI is designed with robust security at its core. Its adaptive AI continuously learns from interaction patterns to better detect anomalies, identify potential threats, and protect confidentiality across team workflows.'
    },
    {
      heading: 'Integrated Collaboration Tools for Productivity',
      content:
        'Alongside security, MedhasAI provides real-time document collaboration, project management support, video communication workflows, and AI-driven insights that help teams reduce friction and stay aligned.'
    },
    {
      heading: 'Adaptive Intelligence for Better User Experience',
      content:
        'MedhasAI adapts to user behavior over time to deliver personalized, intuitive experiences. This helps teams collaborate faster while still maintaining secure and governed access to information.'
    }
  ];

  const productivityArticleSections = [
    {
      heading: 'Idea Management',
      content:
        'The idea management template helps teams capture, evaluate, and prioritize ideas. With AI-driven insights, teams can spot trends, estimate impact, and make better decisions on what to build next.'
    },
    {
      heading: 'Agile Project Planning',
      content:
        'The agile planning template supports backlog refinement, sprint planning, and progress tracking. It keeps cross-functional teams aligned while adapting quickly to changing priorities.'
    },
    {
      heading: 'Requirements Gathering',
      content:
        'This template helps teams capture and structure product requirements using NLP-assisted organization. It reduces misunderstandings and improves clarity across product, design, and engineering.'
    },
    {
      heading: 'User Research and Analysis',
      content:
        'The user research template analyzes feedback, sentiment, and patterns so teams can make data-driven product decisions and prioritize what matters most to users.'
    },
    {
      heading: 'Quality Assurance (QA) Testing',
      content:
        'The QA template automates repetitive testing workflows such as test case generation, execution tracking, and reporting. This improves coverage while reducing manual effort.'
    }
  ];

  const executionArticleSections = [
    {
      heading: 'Meeting Minutes Extraction and Summarization',
      content:
        'MedhasAI uses speech recognition and NLP to capture meeting discussions and summarize key points, decisions, and action items. This helps teams retain critical context even in long or multi-participant meetings.'
    },
    {
      heading: 'Action Item Assignment and Prioritization',
      content:
        'After summarization, MedhasAI can assign action items to team members based on role, expertise, and workload. It also prioritizes work by urgency and expected impact so important tasks are handled first.'
    },
    {
      heading: 'Follow-up Reminders and Progress Tracking',
      content:
        'MedhasAI schedules reminders for action items and tracks progress over time. This keeps ownership clear, improves accountability, and helps teams maintain momentum after meetings.'
    },
    {
      heading: 'Decision-Making Support with AI Insights',
      content:
        'In addition to task management, MedhasAI provides decision support by analyzing relevant signals such as market movement, customer feedback, and competitive trends.'
    },
    {
      heading: 'Integration with Collaboration Tools',
      content:
        'MedhasAI integrates with Google Workspace, Microsoft Teams, and Slack so notes, action items, reminders, and updates remain accessible within the tools teams already use.'
    }
  ];

  const knowledgeBaseArticleSections = [
    {
      heading: 'Intelligent Knowledge Repositories',
      content:
        'MedhasAI builds searchable knowledge repositories that are easy to access across teams. NLP-powered indexing and categorization make information discovery faster and more reliable.'
    },
    {
      heading: 'Collaborative Learning Platforms',
      content:
        'MedhasAI supports collaborative learning through real-time discussions, Q&A, and co-creation workflows. Integrations with tools like Slack and Microsoft Teams help teams share insights in context.'
    },
    {
      heading: 'Expert Finder and Matching',
      content:
        'Its expert finder capability connects users to subject-matter experts based on skills, prior contributions, and domain relevance, helping teams resolve questions quickly and accurately.'
    },
    {
      heading: 'Personalized Learning Paths',
      content:
        'Adaptive AI can suggest personalized learning paths by analyzing role needs, skill level, and goals. This helps employees continuously upskill while staying aligned to team objectives.'
    },
    {
      heading: 'Real-time Updates and Notifications',
      content:
        'MedhasAI keeps distributed teams informed with updates on trends, best practices, and internal changes so knowledge remains current and actionable across the organization.'
    }
  ];

  const handleSubscribe = async (event) => {
    event.preventDefault();

    const normalizedEmail = subscriberEmail.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!normalizedEmail || !emailRegex.test(normalizedEmail)) {
      setSubscribeStatus({
        type: 'error',
        message: 'Please enter a valid email address.',
      });
      return;
    }

    setIsSubscribing(true);
    setSubscribeStatus({ type: '', message: '' });

    try {
      await axios.get(`${apiBaseUrl}/api/subscribe/csrf`, { withCredentials: true });

      const { data } = await axios.post(
        `${apiBaseUrl}/api/subscribe`,
        { email: normalizedEmail },
        { withCredentials: true }
      );

      setSubscribeStatus({
        type: 'success',
        message: data?.message || 'Subscription successful. Check your email for confirmation.',
      });
      setSubscriberEmail('');
    } catch (error) {
      setSubscribeStatus({
        type: 'error',
        message:
          error?.response?.data?.message ||
          'Subscription failed. Please try again in a moment.',
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="blog-container">
      <header className="blog-header">
        <div className="blog-brand" role="img" aria-label="Medhas AI logo and name">
          <div className="blog-brand-live-logo" aria-hidden="true">
            <BubbleNetwork />
          </div>
          <span className="blog-brand-name">Medhas AI</span>
        </div>
        <button onClick={() => navigate('/')} className="blog-back-button">
          ← Back to Home
        </button>
      </header>

      <div className="blog-content">
        <section className="blog-hero">
          <p className="blog-kicker">Medhas AI Journal</p>
          <h1>Ideas, product updates, and practical AI playbooks</h1>
          <p>
            Explore how modern teams use Medhas AI for smarter collaboration,
            secure sharing, and knowledge-driven decisions.
          </p>
        </section>

        <section className="blog-featured">
          <span className="post-chip">Featured</span>
          <h2>{featuredPost.title}</h2>
          <p>{featuredPost.summary}</p>
          <div className="post-meta">
            <span>{featuredPost.category}</span>
            <span>•</span>
            <span>{featuredPost.readTime}</span>
            <span>•</span>
            <span>{featuredPost.date}</span>
          </div>
          <button
            type="button"
            className="blog-read-button"
            onClick={() => setShowFeaturedArticle((prev) => !prev)}
          >
            {showFeaturedArticle ? 'Hide article' : 'Read article'}
          </button>

          {showFeaturedArticle && (
            <div className="featured-article-content">
              <h3>Transforming Team Collaboration with Medhas AI</h3>
              <p>
                Medhas AI is revolutionizing team collaboration by bridging the
                gaps between individuals, fostering a more efficient and cohesive
                work environment. Here is a closer look at how our technology is
                making this possible:
              </p>

              {fullFeaturedArticle.map((section) => (
                <article key={section.heading} className="featured-article-block">
                  <h4>{section.heading}</h4>
                  <p>{section.content}</p>
                </article>
              ))}

              <p className="featured-article-summary">
                In summary, Medhas AI is transforming team collaboration by
                providing intelligent communication solutions that streamline
                workflows, improve efficiency, and foster a positive and
                productive work environment. Whether it is real-time
                communication, intelligent meeting assistance, predictive
                analytics, or continuous learning, our technology is designed to
                help teams collaborate more effectively than ever before.
              </p>
            </div>
          )}
        </section>

        <section className="blog-grid-section">
          <div className="blog-section-head">
            <h3>Recent Posts</h3>
            <p>
              Fresh reads from the Medhas AI team on product, security, and
              practical collaboration systems.
            </p>
          </div>

          <div className="blog-topics" aria-label="Blog topics">
            {topicFilters.map((topic) => (
              <button
                key={topic}
                type="button"
                className={`topic-chip ${selectedTopic === topic ? 'active' : ''}`}
                onClick={() => setSelectedTopic(topic)}
              >
                {topic}
              </button>
            ))}
          </div>

          <div className="blog-grid">
            {filteredPosts.map((post) => (
              <article
                className={`blog-card ${
                  (post.category === 'Collaboration' && showCollaborationArticle) ||
                  (post.category === 'AI & RAG' && showAiRagArticle) ||
                  (post.category === 'Security' && showSecurityArticle) ||
                  (post.category === 'Productivity' && showProductivityArticle) ||
                  (post.category === 'Execution' && showExecutionArticle) ||
                  (post.category === 'Knowledge Base' && showKnowledgeBaseArticle)
                    ? 'blog-card-expanded'
                    : ''
                }`}
                key={post.title}
              >
                <span className="post-chip">{post.category}</span>
                <h4>{post.title}</h4>
                <p>{post.summary}</p>
                <div className="post-meta">
                  <span>{post.readTime}</span>
                  <span>•</span>
                  <span>{post.date}</span>
                </div>

                {post.category === 'Collaboration' && (
                  <>
                    <button
                      type="button"
                      className="blog-read-button"
                      onClick={() => setShowCollaborationArticle((prev) => !prev)}
                    >
                      {showCollaborationArticle ? 'Hide article' : 'Read article'}
                    </button>

                    {showCollaborationArticle && (
                      <div className="featured-article-content">
                        <h4>Enhancing Cross-Functional Communication</h4>
                        <p>
                          Effective cross-functional communication is crucial for the success of any
                          organization. Here are five strategies that can help improve communication
                          within a team:
                        </p>

                        {collaborationArticleSections.map((section) => (
                          <article key={section.heading} className="featured-article-block">
                            <h4>{section.heading}</h4>
                            <p>{section.content}</p>
                          </article>
                        ))}

                        <p className="featured-article-summary">
                          By implementing these strategies, teams can improve cross-functional
                          communication, leading to increased productivity, stronger collaboration,
                          and better project outcomes.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {post.category === 'AI & RAG' && (
                  <>
                    <button
                      type="button"
                      className="blog-read-button"
                      onClick={() => setShowAiRagArticle((prev) => !prev)}
                    >
                      {showAiRagArticle ? 'Hide article' : 'Read article'}
                    </button>

                    {showAiRagArticle && (
                      <div className="featured-article-content">
                        <h4>Building Trustworthy AI Responses with Citations</h4>
                        <p>
                          To ensure AI responses are accurate, reliable, and trustworthy, build
                          responses using credible sources and clear citations.
                        </p>

                        {aiRagArticleSections.map((section) => (
                          <article key={section.heading} className="featured-article-block">
                            <h4>{section.heading}</h4>
                            <p>{section.content}</p>
                          </article>
                        ))}

                        <p className="featured-article-summary">
                          By grounding outputs in verifiable sources and consistent attribution,
                          AI teams can improve accuracy, reliability, and user trust.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {post.category === 'Security' && (
                  <>
                    <button
                      type="button"
                      className="blog-read-button"
                      onClick={() => setShowSecurityArticle((prev) => !prev)}
                    >
                      {showSecurityArticle ? 'Hide article' : 'Read article'}
                    </button>

                    {showSecurityArticle && (
                      <div className="featured-article-content">
                        <h4>
                          Secure Collaboration: The Essential Component for Every Modern Team
                        </h4>
                        <p>
                          In today\'s digital-first environment, secure collaboration is no longer
                          optional. It is foundational for protecting sensitive data while enabling
                          teams to work effectively.
                        </p>

                        {securityArticleSections.map((section) => (
                          <article key={section.heading} className="featured-article-block">
                            <h4>{section.heading}</h4>
                            <p>{section.content}</p>
                          </article>
                        ))}

                        <p className="featured-article-summary">
                          MedhasAI helps teams collaborate securely and productively through adaptive
                          protection, intelligent workflows, and tools built for modern team dynamics.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {post.category === 'Productivity' && (
                  <>
                    <button
                      type="button"
                      className="blog-read-button"
                      onClick={() => setShowProductivityArticle((prev) => !prev)}
                    >
                      {showProductivityArticle ? 'Hide article' : 'Read article'}
                    </button>

                    {showProductivityArticle && (
                      <div className="featured-article-content">
                        <h4>AI Workflow Templates for Product Teams in MedhasAI</h4>
                        <p>
                          MedhasAI offers AI workflow templates that help product teams streamline
                          execution, improve collaboration, and deliver outcomes faster.
                        </p>

                        {productivityArticleSections.map((section) => (
                          <article key={section.heading} className="featured-article-block">
                            <h4>{section.heading}</h4>
                            <p>{section.content}</p>
                          </article>
                        ))}

                        <p className="featured-article-summary">
                          From idea capture to QA, these templates provide a practical system for
                          more consistent and successful product development.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {post.category === 'Execution' && (
                  <>
                    <button
                      type="button"
                      className="blog-read-button"
                      onClick={() => setShowExecutionArticle((prev) => !prev)}
                    >
                      {showExecutionArticle ? 'Hide article' : 'Read article'}
                    </button>

                    {showExecutionArticle && (
                      <div className="featured-article-content">
                        <h4>From Meetings to Action: Transforming Notes into Decisions</h4>
                        <p>
                          MedhasAI helps teams convert meeting conversations into structured,
                          actionable decisions with clear ownership and follow-through.
                        </p>

                        {executionArticleSections.map((section) => (
                          <article key={section.heading} className="featured-article-block">
                            <h4>{section.heading}</h4>
                            <p>{section.content}</p>
                          </article>
                        ))}

                        <p className="featured-article-summary">
                          By connecting notes, priorities, decisions, and execution in one flow,
                          MedhasAI turns meetings into measurable action.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {post.category === 'Knowledge Base' && (
                  <>
                    <button
                      type="button"
                      className="blog-read-button"
                      onClick={() => setShowKnowledgeBaseArticle((prev) => !prev)}
                    >
                      {showKnowledgeBaseArticle ? 'Hide article' : 'Read article'}
                    </button>

                    {showKnowledgeBaseArticle && (
                      <div className="featured-article-content">
                        <h4>Scaling Internal Knowledge Across Distributed Teams</h4>
                        <p>
                          MedhasAI helps globally distributed teams share, discover, and apply
                          internal knowledge efficiently through intelligent and collaborative
                          systems.
                        </p>

                        {knowledgeBaseArticleSections.map((section) => (
                          <article key={section.heading} className="featured-article-block">
                            <h4>{section.heading}</h4>
                            <p>{section.content}</p>
                          </article>
                        ))}

                        <p className="featured-article-summary">
                          By combining searchable knowledge, expert matching, and continuous updates,
                          MedhasAI enables distributed teams to learn faster and innovate together.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </article>
            ))}
          </div>
        </section>

        <section className="blog-guides">
          <div className="blog-section-head">
            <h3>Popular Guides</h3>
            <p>
              Actionable resources your team can apply immediately in planning,
              execution, and knowledge management.
            </p>
          </div>
          <div className="guide-list">
            {guidePosts.map((guide) => (
              <article
                className={`guide-card ${
                  expandedGuideTitle === guide.title ? 'guide-card-expanded' : ''
                }`}
                key={guide.title}
              >
                <h4>{guide.title}</h4>
                <p>{guide.detail}</p>
                <span>{guide.meta}</span>
                <button
                  type="button"
                  className="blog-read-button"
                  onClick={() =>
                    setExpandedGuideTitle((prev) => (prev === guide.title ? '' : guide.title))
                  }
                >
                  {expandedGuideTitle === guide.title ? 'Hide article' : 'Read article'}
                </button>

                {expandedGuideTitle === guide.title && guideArticleDetails[guide.title] && (
                  <div className="featured-article-content">
                    <h4>{guide.title}</h4>
                    <p>{guideArticleDetails[guide.title].intro}</p>

                    {guideArticleDetails[guide.title].sections.map((section) => (
                      <article key={section.heading} className="featured-article-block">
                        <h4>{section.heading}</h4>
                        <p>{section.content}</p>
                      </article>
                    ))}

                    <p className="featured-article-summary">
                      {guideArticleDetails[guide.title].summary}
                    </p>
                    <p>{guideArticleDetails[guide.title].takeaway}</p>
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>

        <section className="blog-subscribe">
          <h3>Stay in the loop</h3>
          <p>
            Get product stories, team productivity insights, and AI best
            practices delivered to your inbox.
          </p>
          <form className="email-signup" onSubmit={handleSubscribe}>
            <input
              type="email"
              placeholder="Enter your work email"
              className="email-input"
              value={subscriberEmail}
              onChange={(event) => setSubscriberEmail(event.target.value)}
              disabled={isSubscribing}
              aria-label="Email address"
              required
            />
            <button type="submit" className="subscribe-button" disabled={isSubscribing}>
              {isSubscribing ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>

          {subscribeStatus.message && (
            <p className={`subscribe-feedback subscribe-feedback-${subscribeStatus.type}`}>
              {subscribeStatus.message}
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

export default Blog;